import { useState, useMemo } from "react";
import {
  useNotificationsQuery,
  useMarkAllAsReadMutation,
} from "@/hooks/api/notification.queries";
import NotificationItem from "@/components/Notifications/NotificationItem";
import NotificationTabs from "@/components/Notifications/NotificationTabs";
import { Spinner, ErrorMessage } from "@/components";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NotificationCategory } from "@/services/notification.service";

const Notifications = () => {
  const { data: notifications, isLoading, error } = useNotificationsQuery();
  const markAllReadMutation = useMarkAllAsReadMutation();
  const [activeTab, setActiveTab] = useState("all");

  const unreadCount = useMemo(
    () => notifications?.filter((n) => !n.isRead).length || 0,
    [notifications]
  );

  const tabs = useMemo(
    () => [
      { id: "all", label: "All" },
      { id: "unread", label: "Unread", count: unreadCount },
      { id: "Placements", label: "Placements" },
      { id: "Internships", label: "Internships" },
      { id: "Programs", label: "Programs" },
    ],
    [unreadCount]
  );

  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    if (activeTab === "all") return notifications;
    if (activeTab === "unread") return notifications.filter((n) => !n.isRead);
    return notifications.filter(
      (n) => n.category === (activeTab as NotificationCategory)
    );
  }, [notifications, activeTab]);

  if (isLoading) return <Spinner />;
  if (error) return <ErrorMessage message="Failed to load notifications." />;

  return (
    <div className="animate-in fade-in mx-auto max-w-6xl space-y-8 p-4 duration-500 md:p-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div className="space-y-1">
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
            Notifications
          </h1>
          <p className="text-sm font-semibold text-gray-400">
            Stay updated with latest activities
          </p>
        </div>
        <Button
          variant="link"
          onClick={() => markAllReadMutation.mutate()}
          className="h-auto h-fit p-0 text-xs font-bold text-gray-900 hover:text-black"
        >
          Mark All as Read
        </Button>
      </div>

      <NotificationTabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      <Card className="border-none bg-transparent shadow-none">
        <div className="space-y-4">
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <Card
                key={notification.id}
                className="overflow-hidden rounded-xl border-none shadow-sm transition-shadow hover:shadow-md"
              >
                <NotificationItem notification={notification} />
              </Card>
            ))
          ) : (
            <div className="space-y-2 py-20 text-center">
              <p className="text-lg font-bold text-gray-900">All caught up!</p>
              <p className="text-sm font-medium text-gray-400">
                You have no notifications in this category.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Notifications;
